import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, CheckCircle2, FlaskConical } from "lucide-react";

const Section = ({ title, desc, children }) => (
  <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 sm:p-6">
    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    {desc && <p className="text-xs text-slate-500 mb-4">{desc}</p>}
    <div className={desc ? "" : "mt-4"}>{children}</div>
  </div>
);

export default function LabNewReport() {
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    report_date: new Date().toISOString().slice(0, 10),
    province: user?.province || "", territory: "", health_zone: user?.health_zone || "",
    health_area: "", laboratory: "", disease: "",
  });
  const [activities, setActivities] = useState([]);
  const [metrics, setMetrics] = useState({});

  const geo = cfg?.geo || {};
  const provinces = Object.keys(geo);
  const territories = form.province ? Object.keys(geo[form.province]?.territories || {}) : [];
  const zones = form.province && form.territory ? geo[form.province]?.territories?.[form.territory] || [] : [];
  const metricLabels = useMemo(() => Object.fromEntries((cfg?.lab_metrics || []).map((m) => [m.code, m.name])), [cfg]);
  const visibleMetrics = useMemo(() => {
    const am = cfg?.lab_activity_metrics || {};
    const set = new Set();
    activities.forEach((a) => (am[a] || []).forEach((m) => set.add(m)));
    return [...set];
  }, [activities, cfg]);

  const toggle = (c) => setActivities((a) => (a.includes(c) ? a.filter((x) => x !== c) : [...a, c]));

  const submit = async () => {
    if (!form.province || !form.health_zone) { toast.error("Province et Zone de Santé requises."); return; }
    if (activities.length === 0) { toast.error("Sélectionnez au moins une activité."); return; }
    setSaving(true);
    try {
      await api.post("/lab/reports", {
        ...form, activities,
        metrics: Object.fromEntries(Object.entries(metrics).map(([k, v]) => [k, Number(v) || 0])),
      });
      toast.success("Rapport laboratoire enregistré.");
      navigate("/lab");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="lab-new-page">
      <PageHeader title={t("Laboratoire · Nouveau Rapport")} subtitle={t("Saisie rapide des activités de laboratoire")} />
      <div className="space-y-4">
        <Section title={t("Identification")}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>{t("Date")}</Label><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} data-testid="lab-date" /></div>
            <div><Label>{t("Laboratoire")}</Label><Input value={form.laboratory} onChange={(e) => setForm({ ...form, laboratory: e.target.value })} placeholder={t("Nom du labo")} /></div>
            <div>
              <Label>{t("Province *")}</Label>
              <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v, territory: "", health_zone: "" })}>
                <SelectTrigger data-testid="lab-province"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("Territoire")}</Label>
              <Select value={form.territory} onValueChange={(v) => setForm({ ...form, territory: v, health_zone: "" })} disabled={!form.province}>
                <SelectTrigger><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{territories.map((tr) => <SelectItem key={tr} value={tr}>{tr}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("Zone de Santé *")}</Label>
              <Select value={form.health_zone} onValueChange={(v) => setForm({ ...form, health_zone: v })} disabled={!form.territory}>
                <SelectTrigger data-testid="lab-zone"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("Maladie / événement")}</Label>
              <Select value={form.disease} onValueChange={(v) => setForm({ ...form, disease: v })}>
                <SelectTrigger data-testid="lab-disease"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{(cfg?.diseases || []).map((d) => <SelectItem key={d} value={d}>{t(d)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        <Section title={t("Activités de laboratoire")} desc={t("Cochez · les champs chiffrés s'afficheront selon vos choix")}>
          <div className="grid sm:grid-cols-2 gap-2">
            {(cfg?.lab_activities || []).map((a) => (
              <label key={a.code} className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${activities.includes(a.code) ? "border-[#0284C7] bg-[#E0F2FE]" : "border-slate-200 hover:bg-slate-50"}`}>
                <Checkbox checked={activities.includes(a.code)} onCheckedChange={() => toggle(a.code)} data-testid={`lab-act-${a.code}`} />
                <span className="text-sm text-slate-700">{a.name}</span>
              </label>
            ))}
          </div>
        </Section>

        {visibleMetrics.length > 0 && (
          <Section title={t("Chiffres")}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleMetrics.map((code) => (
                <div key={code}>
                  <Label className="text-xs">{metricLabels[code]}</Label>
                  <Input type="number" min="0" value={metrics[code] ?? ""} placeholder="0" onChange={(e) => setMetrics({ ...metrics, [code]: e.target.value })} data-testid={`lab-metric-${code}`} />
                </div>
              ))}
            </div>
          </Section>
        )}

        <div className="flex justify-end gap-3 pb-10">
          <Button variant="outline" onClick={() => navigate("/lab")}>{t("Annuler")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="lab-submit" className="bg-[#0284C7] hover:bg-[#0369A1]">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} {t("Enregistrer")}
          </Button>
        </div>
      </div>
    </div>
  );
}
